import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import NavBar from '../components/NavBar';
import { useAuth } from '../AuthContext';

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(Math.max(bytes,1)) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatDuration(secs) {
  if (!secs) return '—';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export default function Recordings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [recordings, setRecordings] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [userFilter, setUserFilter] = useState('');
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [renaming, setRenaming] = useState(null); // recording id being renamed
  const [renameValue, setRenameValue] = useState('');
  const limit = 50;
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [uploadingToDrive, setUploadingToDrive] = useState(null);
  const [driveEnabled, setDriveEnabled] = useState(false);

  // Check if Google Drive is enabled
  React.useEffect(() => {
    api.get('/settings').then(({data}) => {
      setDriveEnabled(data.gdrive_enabled === 'true');
    }).catch(()=>{});
  }, []);

  const handleDriveUpload = async (rec) => {
    setUploadingToDrive(rec.id);
    try {
      await api.post(`/gdrive/upload/${rec.id}`);
      alert('Upload started — check Google Drive in a few minutes');
    } catch(e) {
      alert('Upload failed: ' + e.message);
    }
    setUploadingToDrive(null);
  };
  const [playing, setPlaying] = useState(null); // recording object being played
  const videoRef = useRef(null);

  const loadRecordings = useCallback(async () => {
    try {
      const { data } = await api.get(`/recordings?page=${page}&limit=${limit}&sort_by=${sortBy}&sort_dir=${sortDir}${userFilter ? '&user_id='+userFilter : ''}`);
      setRecordings(data.recordings || data);
      setTotal(data.total || (data.recordings ? data.total : data.length));
    } catch {}
    finally { setLoading(false); }
  }, [page, sortBy, sortDir, userFilter]);

  useEffect(() => { loadRecordings(); }, [loadRecordings]);

  const handleDownload = async (rec) => {
    setDownloading(rec.id);
    try {
      const token = localStorage.getItem('ms_token');
      const response = await fetch(`/api/recordings/${rec.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = rec.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed: ' + err.message);
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (rec) => {
    if (!window.confirm(`Delete recording "${rec.filename}"?\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/recordings/${rec.id}`);
      setRecordings(r => r.filter(x => x.id !== rec.id));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete');
    }
  };

  const totalSize = recordings.reduce((sum, r) => sum + (parseInt(r.size_bytes) || 0), 0);
  const canManageAll = ['admin', 'manager'].includes(user?.role);

  // Load users for filter dropdown (admin only)
  useEffect(() => {
    if (canManageAll) {
      api.get('/auth/users').then(({data}) => setUsers(data)).catch(()=>{});
    }
  }, [canManageAll]);

  // Reset page when filters change
  useEffect(() => { setPage(1); setSelected([]); }, [sortBy, sortDir, userFilter]);

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);

  const handleRename = async (rec) => {
    if (!renameValue.trim() || renameValue === rec.title) { setRenaming(null); return; }
    try {
      await api.patch(`/recordings/${rec.id}`, { title: renameValue.trim() });
      setRecordings(r => r.map(x => x.id === rec.id ? {...x, title: renameValue.trim()} : x));
    } catch { alert('Rename failed'); }
    setRenaming(null);
  };
  const toggleAll = () => setSelected(s => s.length === recordings.length ? [] : recordings.map(r=>r.id));
  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} recording(s)? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await api.delete('/recordings/bulk', { data: { ids: selected } });
      setSelected([]);
      await loadRecordings();
    } catch(e) { alert('Delete failed'); }
    setBulkDeleting(false);
  };

  const s = {
    page: { minHeight: '100vh', background: 'var(--bg)' },
    nav: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px', height: '58px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', position: 'sticky', top: 0, zIndex: 10 },
    navLogo: { display: 'flex', alignItems: 'center', gap: '9px', fontWeight: '800', fontSize: '17px' },
    navDot: { width: '9px', height: '9px', borderRadius: '50%', background: 'var(--signal)', boxShadow: '0 0 8px var(--signal)' },
    navRight: { display: 'flex', alignItems: 'center', gap: '10px' },
    navBtn: (h) => ({ fontSize: '13px', padding: '6px 14px', border: `1px solid ${h ? 'rgba(0,230,118,0.3)' : 'var(--border)'}`, borderRadius: '7px', background: 'transparent', color: h ? 'var(--signal)' : 'var(--text-muted)', cursor: 'pointer' }),
    main: { maxWidth: '960px', margin: '0 auto', padding: '36px 24px' },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px' },
    title: { fontSize: '24px', fontWeight: '800', letterSpacing: '-0.5px' },
    sub: { fontSize: '14px', color: 'var(--text-muted)', marginTop: '4px' },
    statsRow: { display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' },
    statCard: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 18px', flex: 1 },
    statLabel: { fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' },
    statValue: { fontSize: '20px', fontWeight: '800' },
    recList: { display: 'flex', flexDirection: 'column', gap: '8px' },
    recCard: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' },
    recIcon: { width: '40px', height: '40px', borderRadius: '8px', background: 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 },
    recInfo: { flex: 1, minWidth: 0 },
    recTitle: { fontWeight: '600', fontSize: '14px', marginBottom: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    recMeta: { fontSize: '12px', color: 'var(--text-muted)', display: 'flex', gap: '12px', flexWrap: 'wrap' },
    recSize: { fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', flexShrink: 0 },
    actions: { display: 'flex', gap: '6px', flexShrink: 0, marginLeft: 'auto' },
    dlBtn: (loading) => ({ background: loading ? 'var(--surface2)' : 'var(--signal)', color: loading ? 'var(--text-muted)' : '#000', border: 'none', borderRadius: '7px', padding: '6px 14px', fontSize: '12px', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer' }),
    delBtn: { background: 'var(--danger-bg)', border: '1px solid rgba(255,77,77,0.2)', color: 'var(--danger)', borderRadius: '7px', padding: '6px 12px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
    empty: { textAlign: 'center', padding: '64px 24px', background: 'var(--surface)', border: '1px dashed var(--border)', borderRadius: 'var(--radius-lg)', color: 'var(--text-muted)' },
    emptyTitle: { fontSize: '16px', fontWeight: '700', color: 'var(--text)', marginBottom: '8px' },
    streamerBadge: { fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '4px', background: 'rgba(0,230,118,0.08)', color: 'var(--signal)', border: '1px solid rgba(0,230,118,0.15)' },
  };

  return (
        <NavBar>
    <div style={s.page}>

      <div style={s.main}>
        <div style={s.header}>
          <div>
            <div style={s.title}>Recordings</div>
            <div style={s.sub}>
              {canManageAll ? 'All stream recordings' : 'Your stream recordings'}
            </div>
          </div>
        </div>

        <div style={s.statsRow}>
          <div style={s.statCard}>
            <div style={s.statLabel}>Total Recordings</div>
            <div style={s.statValue}>{total || recordings.length}</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statLabel}>Total Size</div>
            <div style={s.statValue}>{formatBytes(totalSize)}</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statLabel}>Total Duration</div>
            <div style={s.statValue}>{formatDuration(recordings.reduce((s, r) => s + (r.duration_secs || 0), 0))}</div>
          </div>
        </div>

        {/* Video Player Modal */}
      {playing && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.9)', zIndex:200, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'24px' }}
          onClick={e => e.target === e.currentTarget && setPlaying(null)}>
          <div style={{ width:'100%', maxWidth:'900px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
              <div style={{ fontWeight:'700', fontSize:'15px', color:'#e8eaf0' }}>{playing.title || playing.filename}</div>
              <button onClick={() => setPlaying(null)}
                style={{ background:'var(--surface2)', border:'1px solid var(--border)', color:'var(--text)', borderRadius:'8px', padding:'6px 14px', cursor:'pointer', fontSize:'13px' }}>
                ✕ Close
              </button>
            </div>
            <video
              ref={videoRef}
              controls
              autoPlay
              style={{ width:'100%', borderRadius:'10px', background:'#000', maxHeight:'70vh' }}
              onError={(e) => console.error('Video error:', e)}
              src={`/api/recordings/${playing.id}/stream?token=${localStorage.getItem('ms_token')}`}
            />
            <div style={{ marginTop:'10px', display:'flex', gap:'10px', justifyContent:'flex-end' }}>
              <button onClick={() => handleDownload(playing)}
                style={{ background:'var(--signal)', color:'#000', border:'none', borderRadius:'7px', padding:'7px 16px', fontSize:'13px', fontWeight:'700', cursor:'pointer' }}>
                ↓ Download
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
          <div style={{ color: 'var(--text-muted)', padding: '32px 0' }}>Loading…</div>
        ) : recordings.length === 0 ? (
          <div style={s.empty}>
            <div style={s.emptyTitle}>No recordings yet</div>
            Enable recording when you start a stream to save it as an MP4 file
          </div>
        ) : (
          <>
          {/* Toolbar */}
          <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'10px',padding:'10px 14px',marginBottom:'14px',display:'flex',gap:'8px',flexWrap:'wrap',alignItems:'center'}}>
            <input type="checkbox" checked={selected.length===recordings.length && recordings.length>0}
              onChange={toggleAll} style={{cursor:'pointer',width:'16px',height:'16px',flexShrink:0}}
              title="Select all"/>
            {selected.length > 0 && (
              <span style={{fontSize:'12px',color:'var(--text-muted)',marginRight:'4px'}}>{selected.length} selected</span>
            )}
            <div style={{width:'1px',height:'20px',background:'var(--border)',margin:'0 4px'}}/>
            <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
              style={{padding:'6px 10px',borderRadius:'7px',border:'1px solid var(--border)',background:'var(--surface2)',color:'var(--text)',fontSize:'12px',flex:1,minWidth:'120px'}}>
              <option value="created_at">📅 Date</option>
              <option value="size_bytes">💾 Size</option>
              <option value="duration_secs">⏱ Duration</option>
            </select>
            <button onClick={()=>setSortDir(d=>d==='desc'?'asc':'desc')}
              style={{padding:'6px 10px',borderRadius:'7px',border:'1px solid var(--border)',background:'var(--surface2)',color:'var(--text)',fontSize:'12px',cursor:'pointer',whiteSpace:'nowrap'}}>
              {sortDir==='desc'?'↓ Newest first':'↑ Oldest first'}
            </button>
            {canManageAll && users.length > 0 && (
              <select value={userFilter} onChange={e=>setUserFilter(e.target.value)}
                style={{padding:'6px 10px',borderRadius:'7px',border:'1px solid var(--border)',background:'var(--surface2)',color:'var(--text)',fontSize:'12px',flex:1,minWidth:'100px'}}>
                <option value="">👥 All DJs</option>
                {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            )}
            {selected.length > 0 && (
              <button onClick={handleBulkDelete} disabled={bulkDeleting}
                style={{padding:'6px 14px',borderRadius:'7px',border:'1px solid rgba(220,38,38,0.3)',background:'var(--danger-bg)',color:'var(--danger)',fontSize:'12px',fontWeight:'700',cursor:'pointer',whiteSpace:'nowrap',marginLeft:'auto'}}>
                {bulkDeleting ? 'Deleting…' : `🗑 Delete ${selected.length}`}
              </button>
            )}
          </div>
          <div style={s.recList}>
            {recordings.map(rec => (
              <div key={rec.id} style={{...s.recCard, outline: selected.includes(rec.id) ? '2px solid var(--signal)' : 'none'}}>
                <input type="checkbox" checked={selected.includes(rec.id)} onChange={()=>toggleSelect(rec.id)} style={{cursor:'pointer',flexShrink:0}}/>
                <div style={{width:'80px',height:'45px',borderRadius:'6px',overflow:'hidden',background:'#000',flexShrink:0,position:'relative'}}>
                  <img
                    src={`/api/thumbnails/video?path=${encodeURIComponent(rec.path)}`}
                    alt={rec.title}
                    style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}
                    onError={e=>{e.target.style.display='none';e.target.parentNode.innerHTML='<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:18px;background:#111">🎬</div>';}}
                  />
                </div>
                <div style={s.recInfo}>
                  <div style={s.recTitle}>
                    {renaming === rec.id ? (
                      <input
                        value={renameValue}
                        onChange={e=>setRenameValue(e.target.value)}
                        onBlur={()=>handleRename(rec)}
                        onKeyDown={e=>{if(e.key==='Enter')handleRename(rec);if(e.key==='Escape')setRenaming(null);}}
                        autoFocus
                        style={{width:'100%',padding:'2px 6px',borderRadius:'4px',border:'1px solid var(--signal)',background:'var(--surface2)',color:'var(--text)',fontSize:'14px',fontWeight:'600'}}
                      />
                    ) : (
                      <span
                        title="Click to rename"
                        style={{cursor:'pointer'}}
                        onClick={()=>{setRenaming(rec.id);setRenameValue(rec.title||rec.filename);}}
                      >
                        {rec.title || rec.filename}
                        <span style={{fontSize:'10px',color:'var(--text-muted)',marginLeft:'6px',opacity:0.6}}>✏</span>
                      </span>
                    )}
                  </div>
                  <div style={s.recMeta}>
                    <span>{new Date(rec.created_at).toLocaleString()}</span>
                    <span>{formatDuration(rec.duration_secs)}</span>
                    {canManageAll && rec.streamer_name && (
                      <span style={s.streamerBadge}>{rec.streamer_name}</span>
                    )}
                    <span style={{ fontFamily: 'var(--mono)', fontSize: '11px' }}>{rec.filename}</span>
                  </div>
                </div>
                <div style={s.recSize}>{formatBytes(parseInt(rec.size_bytes) || 0)}</div>
                <div style={s.actions}>
                  <button
                    style={{ background:'var(--surface2)', border:'1px solid var(--border)', color:'var(--text)', borderRadius:'7px', padding:'6px 12px', fontSize:'12px', fontWeight:'600', cursor:'pointer' }}
                    onClick={() => setPlaying(rec)}
                  >
                    ▶ Play
                  </button>
                  <button
                    style={s.dlBtn(downloading === rec.id)}
                    onClick={() => handleDownload(rec)}
                    disabled={downloading === rec.id}
                  >
                    {downloading === rec.id ? 'Downloading…' : '↓ Download'}
                  </button>
                  {driveEnabled && (
                    <button
                      style={{...s.dlBtn(false), background: rec.gdrive_status==='uploaded' ? 'rgba(0,200,100,0.1)' : 'var(--surface2)', color: rec.gdrive_status==='uploaded' ? '#00c864' : 'var(--text-muted)', border: '1px solid var(--border)'}}
                      onClick={() => handleDriveUpload(rec)}
                      disabled={uploadingToDrive === rec.id || rec.gdrive_status === 'uploading'}
                      title={rec.gdrive_link ? 'Uploaded to Drive — click to re-upload' : 'Upload to Google Drive'}
                    >
                      {rec.gdrive_status === 'uploaded' ? '☁️ Backed up' :
                       rec.gdrive_status === 'uploading' ? '☁️ Uploading…' :
                       uploadingToDrive === rec.id ? '☁️ Starting…' : '☁️ Drive'}
                    </button>
                  )}
                  <button style={s.delBtn} onClick={() => handleDelete(rec)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
          {/* Pagination */}
          {total > limit && (
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'12px',marginTop:'24px',paddingBottom:'24px'}}>
              <button
                style={{padding:'8px 18px',borderRadius:'8px',border:'1px solid var(--border)',background:'var(--surface)',color:'var(--text)',cursor:'pointer',fontWeight:'600',opacity:page===1?0.4:1}}
                disabled={page===1}
                onClick={()=>setPage(p=>p-1)}
              >← Previous</button>
              <span style={{fontSize:'13px',color:'var(--text-muted)'}}>
                Page {page} of {Math.ceil(total/limit)} ({total} total)
              </span>
              <button
                style={{padding:'8px 18px',borderRadius:'8px',border:'1px solid var(--border)',background:'var(--surface)',color:'var(--text)',cursor:'pointer',fontWeight:'600',opacity:page>=Math.ceil(total/limit)?0.4:1}}
                disabled={page>=Math.ceil(total/limit)}
                onClick={()=>setPage(p=>p+1)}
              >Next →</button>
            </div>
          )}
          </>
        )}
      </div>
    </div>
    </NavBar>
  );
}
